import {Errors, KAError} from "@repo/logging";

import type {
    ContentDescriptor,
    LearnableContentKind,
    ResolvedContentPath,
    ResolvedLearnableContentPath,
} from "../../types/types.ts";

// Create a key (it's a string, but generally consider it an opaque key) that
// represents a given ResolvedContentPath. Can be used to compare
// ResolveContentPath object's data.
export const keyForResolvedContentPath = (
    resolvedContentPath: ResolvedContentPath,
): string => {
    return (
        [
            resolvedContentPath.domainId,
            resolvedContentPath.courseId,
            resolvedContentPath.unitId,
            resolvedContentPath.lessonId,
            resolvedContentPath.learnableNodeKind,
            resolvedContentPath.learnableNodeId,
        ]
            // Remove null values but maintain empty strings since null means
            // not relevant (ex: resolved Lessons have the LC data as null) vs
            // missing (ex: resolved Learnable Content that we don't have the ID
            // for yet). We don't want the Lesson and LearnableContent path to
            // match.
            .filter((p) => p !== null)
            .join("/")
    );
};

// Compare two ResolvedContentPath objects. True if they contain the same
// resolved content, false otherwise.
export const arePathsEqual = (
    path1: ResolvedContentPath,
    path2: ResolvedContentPath,
): boolean => {
    return (
        keyForResolvedContentPath(path1) === keyForResolvedContentPath(path2)
    );
};

// Given the path, return the ContentDescriptor for the leaf node.
export function getContentDescriptorFromPath(
    path: ResolvedLearnableContentPath,
): ContentDescriptor {
    return `${path.learnableNodeKind}:${path.learnableNodeId}`;
}

// Throws an error if path is not a learnable content path.
// If kind is provided, throws an error if the path is not to that kind of
// learnable content.
// Returns the path if it is a learnable content path, typed as a
// ResolvedLearnableContentPath to satisfy TypeScript for downstream callers.
export function refineLearnableContentPathType(
    path: ResolvedContentPath,
    kind?: LearnableContentKind,
): ResolvedLearnableContentPath {
    if (
        !path.learnableNodeKind ||
        path.learnableNodeKind === "CourseChallenge" ||
        path.learnableNodeKind === "MasteryChallenge" ||
        (kind && path.learnableNodeKind !== kind)
    ) {
        throw new KAError(
            `Expected ${kind ?? "learnable content"} but got ${
                path?.learnableNodeKind ?? "a curation node"
            }`,
            Errors.Internal,
        );
    }

    return path;
}
